import EggLogo from '../../assets/egg_logo.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { LoadingBolt } from '../../components/LoadingBolt'
import { MediaBtnContainer } from './components/Buttons'
import { MyVideo } from './components/MyVideo'
import { PreMeetingAudioControls } from './components/PreMeetingAudioControls'
import { PreMeetingCameraControls } from './components/PreMeetingCameraControls'
import Styles from './PreMeeting.module.css'
import { useMyAudio } from './hooks/useMyAudio'
import { useTranslation } from 'react-i18next'
import { useZoomContext } from '../../context/ZoomContext'
import { useMe } from '../meeting-view/hooks/useMe'
import { useUserCourses } from '../meeting-view/hooks/useCourses'
import * as Sentry from '@sentry/react'
import { capitalizeAll } from '../../helpers/formatters'
import { LanguageSelector } from '../../components/LanguageSelector'
import { SentryFeedbackButton } from '../meeting-view/components/Dev/SentryFeedbackButton'
import NoPermission from './components/NoPermission'
import { usePermissions } from '../../context/PermissionsContext'
import DevicesBusy from './components/DevicesBusy'
import { useNavigate } from 'react-router-dom'
import DevicesNotFound from './components/DevicesNotFound'
import { setSessionTags } from '../../helpers/sentry'
import { useEffect } from 'react'
import { useSessionParams } from '../../hooks/useSessionParams'
import { IntercomSelector } from '../../components/IntercomSelector'

export const PreMeeting = ({ check = false }: { check?: boolean }) => {
    useZoomContext()
    const { user } = useMe()
    const { currentCourse } = useUserCourses()
    const navigate = useNavigate()
    user && Sentry.setUser({ email: user.email, id: user.id, username: user.displayNameOrName })
    const { camera, microphone, checking, devicesBusy, devicesNotFound } = usePermissions()
    const { room, _classId, _algorithmId } = useSessionParams()
    const permissionGranted = camera && microphone
    useMyAudio()
    const { t } = useTranslation('premeeting')

    useEffect(() => {
        console.log('PreMeeting component mounted')
        return () => {
            console.log('PreMeeting component unmounted')
        }
    }, [])

    useEffect(() => {
        if (currentCourse) {
            try {
                setSessionTags({
                    courseName: currentCourse.courseName,
                    room: room,
                    _classId: _classId,
                    _algorithmId: _algorithmId,
                })
            } catch (error) {
                console.error('Error setting session tags:', error)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCourse])

    if (checking) return <LoadingBolt />

    const title = check ? t('check-title') : t('join-title')

    return (
        <div className={Styles.container}>
            <section className={Styles.section}>
                <div className={Styles.vendorContainer} style={{ paddingBottom: '10px' }}>
                    <img src={EggLogo} alt="Egg Logo" height={'50px'} />
                </div>
                <h1>
                    {currentCourse
                        ? `${capitalizeAll(currentCourse?.courseName) ?? ''} / ${
                              capitalizeAll(user?.organizationName) ?? ''
                          }`
                        : title}
                </h1>
                <div className={Styles.preview}>
                    {devicesBusy && <DevicesBusy />}
                    {devicesNotFound && <DevicesNotFound />}
                    {!devicesBusy && !devicesNotFound && !permissionGranted && <NoPermission />}
                    {!devicesBusy && !devicesNotFound && permissionGranted && <MyVideo />}
                </div>
                <nav>
                    <PreMeetingAudioControls />
                    <PreMeetingCameraControls />

                    <MediaBtnContainer
                        active={true}
                        accent={true}
                        wide={true}
                        disabled={!camera || !microphone || devicesBusy || devicesNotFound}
                        className="wide accent"
                        onClick={() => {
                            if (check) {
                                navigate('/check/success')
                            } else {
                                navigate('/meeting')
                                sessionStorage.setItem('isPreMeetingDone', 'true')
                            }
                        }}
                        style={{ fontWeight: 'bold' }}
                        id="join-meeting-btn"
                    >
                        <FontAwesomeIcon
                            className={'base-icon'}
                            icon={['fas', check ? 'check-circle' : 'circle-video']}
                            fixedWidth
                            style={{ marginRight: '5px' }}
                        />
                        <span>{t(check ? 'End test' : 'Live')}</span>
                    </MediaBtnContainer>
                </nav>
            </section>
            <div className={Styles.footer}>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <SentryFeedbackButton />
                    <IntercomSelector />
                </div>
                <LanguageSelector/>
            </div>
        </div>
    )
}
